import React, { useState } from "react";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Globe, Grid3X3, Edit, AlertCircle, RotateCcw, Info } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";

interface DeviceBrowserCategoriesTableProps {
  deviceId: string;
}
export const DeviceBrowserCategoriesTable = ({
  deviceId
}: DeviceBrowserCategoriesTableProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const [optimisticOverrides, setOptimisticOverrides] = useState<Record<string, boolean>>({});
  const {
    toast
  } = useToast();
  const {
    categories
  } = useWebsiteCategories();

  // Get device details
  const deviceData = useConvexQuery(api.devices.get.details, deviceId ? {
    device: deviceId as any
  } : "skip");

  // Get device browser configuration using the correct policies API
  const deviceBrowserConfig = useConvexQuery(api.devices.policies.get, deviceData?.device?.deviceConfig ? {
    deviceConfig: deviceData.device.deviceConfig
  } : "skip");


  // Mutation for updating device browser settings using policies API
  const updateDeviceBrowser = useConvexMutation(api.devices.policies.update);
  const isLoading = deviceData === undefined || (deviceData?.device?.deviceConfig && deviceBrowserConfig === undefined);
  // Get the status of a category for the device
  const getCategoryStatus = (categoryId: string): string => {
    // Find the category name from the ID
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return 'blocked'; // Default if category not found

    // First check optimistic overrides for immediate UI feedback
    if (optimisticOverrides.hasOwnProperty(category.name)) {
      return optimisticOverrides[category.name] ? 'blocked' : 'allowed';
    }

    // Then check if there's a device override
    // The categories property shows the status, where "device" means blocked, false means allowed
    const deviceCategories = deviceBrowserConfig?.categories || {};
    if (deviceCategories.hasOwnProperty(category.name)) {
      const override = deviceCategories[category.name];
      return override === "device" ? 'blocked' : 'allowed';
    }

    // No device override, use default allowed status
    return 'allowed';
  };
  // Get the source of a category's status (where it comes from)
  const getCategorySource = (categoryId: string): string => {
    // Find the category name from the ID
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return 'Level';

    // First check optimistic overrides
    if (optimisticOverrides.hasOwnProperty(category.name)) {
      return 'Device';
    }

    // Check if there's a device override in categories
    const deviceCategories = deviceBrowserConfig?.categories || {};
    if (deviceCategories.hasOwnProperty(category.name)) {
      return 'Device';
    }
    
    return 'Level';
  };
  const getLevelStatus = (categoryId: string): string => {
    // Find the category name from the ID
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return 'allowed'; // Default if category not found

    // For now, assume all categories are allowed by level (this needs to be adjusted based on your data model)
    return 'allowed';
  };
  const getRowBackgroundColor = (status: string) => {
    if (status === 'allowed') {
      return 'bg-green-50 hover:bg-green-100';
    } else if (status === 'blocked') {
      return 'bg-red-50 hover:bg-red-100';
    }
    return 'hover:bg-gray-50';
  };
  const handleStatusChangeLocal = (categoryId: string, newStatus: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [categoryId]: newStatus
    }));
  };
  const handleSaveChanges = async () => {
    try {
      console.log('=== SAVE CHANGES DEBUG ===');
      console.log('Pending changes:', pendingChanges);
      console.log('Categories available:', categories.map(c => ({ id: c.id, name: c.name })));
      console.log('Current device browser config:', deviceBrowserConfig);
      
      // Build policy overrides for the mutation - START WITH EXISTING DEVICE OVERRIDES
      const policyOverrides: Record<string, boolean> = {};
      const newOptimisticOverrides: Record<string, boolean> = { ...optimisticOverrides };

      // First, preserve all existing device-level category overrides
      const deviceCategories = deviceBrowserConfig?.categories || {};
      console.log('All category sources in deviceCategories:', deviceCategories);
      
      Object.entries(deviceCategories).forEach(([categoryName, source]) => {
        console.log(`Processing category "${categoryName}" with source:`, source, `(type: ${typeof source})`);
        
        // A category has a device override if source is "device" (blocked) or false/true (explicit allow/block)
        // If source is "level", there's no device override - skip it
        if (source === "level") {
          console.log(`  → Skipping "${categoryName}" - uses level default`);
          return; // No device override, skip
        }
        
        if (source === "device") {
          // This category is blocked at device level
          console.log(`  → Preserving "${categoryName}" as BLOCKED`);
          policyOverrides[categoryName] = true;
        } else if (source === false) {
          // This category is explicitly allowed at device level
          console.log(`  → Preserving "${categoryName}" as ALLOWED`);
          policyOverrides[categoryName] = false;
        } else {
          // Unknown source - log it for debugging
          console.log(`  → Unknown source value for "${categoryName}":`, source);
        }
      });

      console.log('Existing device overrides after preservation:', policyOverrides);

      // Then apply pending changes - use category name instead of ID
      Object.entries(pendingChanges).forEach(([categoryId, newStatus]) => {
        const category = categories.find(cat => cat.id === categoryId);
        
        if (category) {
          // Set the policy override: true = blocked, false = allowed
          const shouldBlock = newStatus === 'blocked';
          policyOverrides[category.name] = shouldBlock;
          newOptimisticOverrides[category.name] = shouldBlock;
        }
      });

      console.log('Final policy overrides to send:', policyOverrides);

      // Set optimistic overrides immediately for instant UI feedback
      setOptimisticOverrides(newOptimisticOverrides);

      // Use the correct policies API
      if (updateDeviceBrowser) {
        // Preserve all existing values when updating categories
        const currentBlockedUrls = deviceBrowserConfig?.blocklist ? 
          Object.keys(deviceBrowserConfig.blocklist) : [];
        const currentAllowedUrls = deviceBrowserConfig?.allowlist ? 
          Object.keys(deviceBrowserConfig.allowlist) : [];
        
        const updateParams = {
          device: deviceId as any,
          policyOverrides,
          blockedUrls: currentBlockedUrls,
          allowedUrls: currentAllowedUrls,
        };
        
        console.log('Update params with all values:', updateParams);
        
        // Perform the mutation - Convex will automatically update the query cache
        await updateDeviceBrowser(updateParams);
        
        // Clear pending changes and exit edit mode
        setPendingChanges({});
        setIsEditMode(false);
        
        toast({
          title: "Changes Saved",
          description: `${Object.keys(pendingChanges).length} category changes have been saved.`
        });

        // Don't clear optimistic overrides automatically - let the query update handle it
        // The optimistic overrides will naturally be ignored once real data is available
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      // Clear optimistic overrides on error
      setOptimisticOverrides({});
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleCancelChanges = () => {
    setPendingChanges({});
    setOptimisticOverrides({});
    setIsEditMode(false);
  };
  const handleRevertToDefaults = async () => {
    try {
      // Set optimistic override to empty for immediate UI feedback
      setOptimisticOverrides({});
      
      if (updateDeviceBrowser) {
        // Preserve all existing values when reverting categories
        const currentBlockedUrls = deviceBrowserConfig?.blocklist ? 
          Object.keys(deviceBrowserConfig.blocklist) : [];
        const currentAllowedUrls = deviceBrowserConfig?.allowlist ? 
          Object.keys(deviceBrowserConfig.allowlist) : [];
        
        await updateDeviceBrowser({
          device: deviceId as any,
          policyOverrides: {},
          blockedUrls: currentBlockedUrls,
          allowedUrls: currentAllowedUrls,
        });
      }
      setIsEditMode(false);
      toast({
        title: "Reverted to Defaults",
        description: "All category overrides have been removed. Using level defaults."
      });
    } catch (error) {
      console.error('Error reverting to defaults:', error);
      // Reset optimistic overrides on error
      setOptimisticOverrides({});
      toast({
        title: "Error",
        description: "Failed to revert to defaults. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleAllowAll = async () => {
    try {
      // Build policy overrides - set all categories to allowed (false)
      const policyOverrides: Record<string, boolean> = {};
      const newOptimisticOverrides: Record<string, boolean> = {};
      
      categories.forEach(category => {
        policyOverrides[category.name] = false; // Allow all
        newOptimisticOverrides[category.name] = false;
      });
      
      // Set optimistic overrides immediately for instant UI feedback
      setOptimisticOverrides(newOptimisticOverrides);
      
      if (updateDeviceBrowser) {
        // Preserve all existing values when allowing all categories
        const currentBlockedUrls = deviceBrowserConfig?.blocklist ? 
          Object.keys(deviceBrowserConfig.blocklist) : [];
        const currentAllowedUrls = deviceBrowserConfig?.allowlist ? 
          Object.keys(deviceBrowserConfig.allowlist) : [];
        
        await updateDeviceBrowser({
          device: deviceId as any,
          policyOverrides,
          blockedUrls: currentBlockedUrls,
          allowedUrls: currentAllowedUrls,
        });
      }
      
      // Clear optimistic overrides after successful update
      setOptimisticOverrides({});
      
      toast({
        title: "All Categories Allowed",
        description: "All website categories have been allowed for this device."
      });
    } catch (error) {
      console.error('Error allowing all categories:', error);
      // Reset optimistic overrides on error
      setOptimisticOverrides({});
      toast({
        title: "Error",
        description: "Failed to allow all categories. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleBlockAll = async () => {
    try {
      // Build policy overrides - set all categories to blocked (true)
      const policyOverrides: Record<string, boolean> = {};
      const newOptimisticOverrides: Record<string, boolean> = {};
      
      categories.forEach(category => {
        policyOverrides[category.name] = true; // Block all
        newOptimisticOverrides[category.name] = true;
      });
      
      // Set optimistic overrides immediately for instant UI feedback
      setOptimisticOverrides(newOptimisticOverrides);
      
      if (updateDeviceBrowser) {
        // Preserve all existing values when blocking all categories
        const currentBlockedUrls = deviceBrowserConfig?.blocklist ? 
          Object.keys(deviceBrowserConfig.blocklist) : [];
        const currentAllowedUrls = deviceBrowserConfig?.allowlist ? 
          Object.keys(deviceBrowserConfig.allowlist) : [];
        
        await updateDeviceBrowser({
          device: deviceId as any,
          policyOverrides,
          blockedUrls: currentBlockedUrls,
          allowedUrls: currentAllowedUrls,
        });
      }
      
      // Clear optimistic overrides after successful update
      setOptimisticOverrides({});
      
      toast({
        title: "All Categories Blocked",
        description: "All website categories have been blocked for this device."
      });
    } catch (error) {
      console.error('Error blocking all categories:', error);
      // Reset optimistic overrides on error
      setOptimisticOverrides({});
      toast({
        title: "Error",
        description: "Failed to block all categories. Please try again.",
        variant: "destructive"
      });
    }
  };
  if (isLoading) {
    return <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span>Loading website categories...</span>
        </CardContent>
      </Card>;
  }
  if (!deviceData?.device?.deviceConfig) {
    return <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-amber-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Device Configuration</h3>
          <p className="text-gray-600 text-center">
            This device has no configuration. Please configure the device to see website categories.
          </p>
        </CardContent>
      </Card>;
  }
  return <div className="space-y-4">
      <div className="flex justify-between items-start">
        
        <div className="flex gap-2">
          {isEditMode ? <>
              <Button variant="outline" size="sm" onClick={handleCancelChanges}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges} disabled={Object.keys(pendingChanges).length === 0}>
                Save Changes
              </Button>
            </> : <>
              <Button variant="outline" size="sm" onClick={handleAllowAll}>
                Allow All
              </Button>
              <Button variant="outline" size="sm" onClick={handleBlockAll}>
                Block All
              </Button>
              {deviceData?.level && <Button variant="outline" size="sm" onClick={handleRevertToDefaults}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Revert to level defaults
                </Button>}
              <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
            </>}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-0">Category</TableHead>
                <TableHead className="w-20 sm:w-32">Status</TableHead>
                {deviceData?.level && <TableHead className="hidden sm:table-cell w-20">Source</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map(category => {
              const status = getCategoryStatus(category.id);
              const source = getCategorySource(category.id);
              return <TableRow key={category.id} className={getRowBackgroundColor(status)}>
                    <TableCell className="min-w-0">
                      <div className="font-medium text-sm truncate">{category.name}</div>
                      <div className="text-xs text-gray-500 sm:hidden">
                        {deviceData?.level && `${source} • `}{status}
                      </div>
                    </TableCell>
                    <TableCell className="w-20 sm:w-32">
                       {isEditMode ? <Select value={pendingChanges[category.id] || getCategoryStatus(category.id)} onValueChange={value => handleStatusChangeLocal(category.id, value)}>
                          <SelectTrigger className="w-full text-xs sm:text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white z-50">
                            <SelectItem value="allowed">
                              <div className="flex items-center justify-between w-full">
                                <span>Allow</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="blocked">
                              <div className="flex items-center justify-between w-full">
                                <span>Block</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select> : <div className="flex items-center gap-2">
                          <Badge variant={status === 'allowed' ? 'default' : 'destructive'} className="text-xs">
                            {status === 'allowed' ? 'Allowed' : 'Blocked'}
                          </Badge>
                        </div>}
                    </TableCell>
                    {deviceData?.level && <TableCell className="hidden sm:table-cell w-20">
                        <span className="text-sm capitalize">{source}</span>
                      </TableCell>}
                  </TableRow>;
            })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isEditMode && <div className="text-xs text-gray-500 p-4 bg-blue-50 rounded-lg">
          <p><strong>Edit Mode:</strong> Use the dropdowns to change category permissions for this device. 
          Click "Save Changes" to apply your changes or "Cancel" to discard them.</p>
        </div>}
    </div>;
};